import { DEVICE_SHELL_VALUES, type DeviceShell } from "@/lib/device/shell";
import { ALL_APP_LOCALES, type AppLocale } from "@/lib/i18n/constants";

export const EXPERIENCE_VARIANT_REGISTRY_KIND = "experience_variant_registry";
export const EXPERIENCE_VARIANT_REGISTRY_VERSION = "2026-06-13-control-v1";
export const EXPERIENCE_VARIANT_REGISTRY_SOURCE_CRM_VERSION = "repo-control-fallback";
export const EXPERIENCE_VARIANT_REGISTRY_GENERATED_AT = "2026-06-13T00:00:00.000Z";
export const EXPERIENCE_VARIANT_REGISTRY_MAX_AGE_SECONDS = 315_360_000;
export const EXPERIENCE_VARIANT_REGISTRY_CHECKSUM_ALGORITHM = "sha256";
export const EXPERIENCE_VARIANT_REGISTRY_SCRIPT_ID = "seoh-experience-variant-registry";

export type ExperienceRegistryStatus = "active" | "paused" | "archived";
export type ExperienceRegistryFallbackPolicy = "control_static";
export type ExperienceRegistryMotionMode = "full" | "reduced" | "static";
export type ExperienceRegistryComplianceGate = "blocked" | "control_static" | "not_applicable" | "passed" | "pending";
export type ExperienceRegistryGoalEvent =
  | "component_engaged"
  | "component_impression"
  | "cta_engaged"
  | "cta_impression"
  | "experience_variant_exposed"
  | "experience_zone_engaged"
  | "experience_zone_viewed"
  | "parallax_variant_engaged"
  | "parallax_variant_viewed";

export type ExperienceVariantRegistryRow = {
  readonly experience_id: string;
  readonly status: ExperienceRegistryStatus;
  readonly route_template_id: string;
  readonly route_pattern: string;
  readonly route_paths: readonly string[];
  readonly component_id: string;
  readonly component_family: string;
  readonly xp_zone: "card" | "cta" | "hero" | "panel" | "progress" | "section";
  readonly xp_depth: number;
  readonly allowed_motion_modes: readonly ExperienceRegistryMotionMode[];
  readonly allowed_device_shells: readonly DeviceShell[];
  readonly locale_scope: readonly AppLocale[];
  readonly content_theme: string;
  readonly campaign_key: string;
  readonly cta_id?: string;
  readonly variant_key: string;
  readonly primary_goal_event: ExperienceRegistryGoalEvent;
  readonly secondary_goal_event?: ExperienceRegistryGoalEvent;
  readonly adult_review_required: boolean;
  readonly vertical_18plus: boolean;
  readonly marketing_compliance_gate: ExperienceRegistryComplianceGate;
};

export type ExperienceVariantRegistryExport = {
  readonly kind: typeof EXPERIENCE_VARIANT_REGISTRY_KIND;
  readonly registry_version: string;
  readonly generated_at: string;
  readonly source_crm_version: string;
  readonly checksum_algorithm: typeof EXPERIENCE_VARIANT_REGISTRY_CHECKSUM_ALGORITHM;
  readonly checksum: string;
  readonly max_age_seconds: number;
  readonly fallback_policy: ExperienceRegistryFallbackPolicy;
  readonly variants: readonly ExperienceVariantRegistryRow[];
};

export type ExperienceVariantQuery = {
  readonly routeTemplateId?: string;
  readonly componentId?: string;
  readonly locale?: string;
  readonly shell?: string;
  readonly motionMode?: string;
};

export type ExperienceVariantResolveOptions = {
  readonly checksumValid?: boolean;
  readonly now?: Date;
};

const ALL_MOTION_MODES = ["full", "reduced", "static"] as const satisfies readonly ExperienceRegistryMotionMode[];
const BROWSER_SAFE_LOCALES = ALL_APP_LOCALES;
const BROWSER_SAFE_SHELLS = DEVICE_SHELL_VALUES;

const CONTROL_VARIANTS = [
  controlVariant({
    experienceId: "xp.home.hero.control",
    routeTemplateId: "home",
    routePattern: "/",
    routePaths: ["/"],
    componentId: "home.hero.shell",
    componentFamily: "hero",
    xpZone: "hero",
    xpDepth: 3,
    contentTheme: "home",
    campaignKey: "camp.home.hero.control",
    primaryGoalEvent: "cta_engaged",
  }),
  controlVariant({
    experienceId: "xp.home.pricing_band.control",
    routeTemplateId: "home",
    routePattern: "/",
    routePaths: ["/"],
    componentId: "home.pricing.band",
    componentFamily: "pricing_band",
    xpZone: "card",
    xpDepth: 2,
    contentTheme: "pricing",
    campaignKey: "camp.home.pricing.control",
    primaryGoalEvent: "cta_engaged",
    secondaryGoalEvent: "component_impression",
  }),
  controlVariant({
    experienceId: "xp.home.lead_magnet.control",
    routeTemplateId: "home",
    routePattern: "/",
    routePaths: ["/"],
    componentId: "home.resources.featured",
    componentFamily: "lead_magnet",
    xpZone: "card",
    xpDepth: 2,
    contentTheme: "resources",
    campaignKey: "camp.home.resources.control",
    primaryGoalEvent: "cta_engaged",
  }),
  controlVariant({
    experienceId: "xp.home.fit-quiz.control",
    routeTemplateId: "home",
    routePattern: "/",
    routePaths: ["/"],
    componentId: "home.fit-quiz.panel",
    componentFamily: "gamification",
    xpZone: "panel",
    xpDepth: 2,
    contentTheme: "gamification",
    campaignKey: "camp.home.fit-quiz.control",
    primaryGoalEvent: "component_engaged",
  }),
  controlVariant({
    experienceId: "xp.home.roi-calculator.control",
    routeTemplateId: "home",
    routePattern: "/",
    routePaths: ["/"],
    componentId: "home.roi-calculator.panel",
    componentFamily: "calculator",
    xpZone: "panel",
    xpDepth: 2,
    contentTheme: "roi",
    campaignKey: "camp.home.roi.control",
    primaryGoalEvent: "cta_engaged",
  }),
  controlVariant({
    experienceId: "xp.service.hero.control",
    routeTemplateId: "services/[slug]",
    routePattern: "/services/*",
    routePaths: ["/services/*"],
    componentId: "service.*.hero",
    componentFamily: "service_hero",
    xpZone: "hero",
    xpDepth: 3,
    contentTheme: "service",
    campaignKey: "camp.service.hero.control",
    primaryGoalEvent: "cta_engaged",
  }),
  controlVariant({
    experienceId: "xp.services.service_grid.control",
    routeTemplateId: "services",
    routePattern: "/services",
    routePaths: ["/services"],
    componentId: "services.index.service-grid",
    componentFamily: "service_grid",
    xpZone: "section",
    xpDepth: 2,
    contentTheme: "services",
    campaignKey: "camp.services.index.control",
    primaryGoalEvent: "component_engaged",
  }),
  controlVariant({
    experienceId: "xp.services.lead_form.control",
    routeTemplateId: "services",
    routePattern: "/services",
    routePaths: ["/services"],
    componentId: "services.index.lead-drawer.form",
    componentFamily: "lead_form",
    xpZone: "panel",
    xpDepth: 3,
    contentTheme: "lead_capture",
    campaignKey: "camp.services.lead-drawer.control",
    primaryGoalEvent: "cta_engaged",
  }),
  controlVariant({
    experienceId: "xp.direct-contact.panel.control",
    routeTemplateId: "contact",
    routePattern: "/*",
    routePaths: ["/*"],
    componentId: "direct-contact.panel",
    componentFamily: "contact_panel",
    xpZone: "panel",
    xpDepth: 2,
    contentTheme: "contact",
    campaignKey: "camp.contact.panel.control",
    primaryGoalEvent: "cta_engaged",
  }),
  controlVariant({
    experienceId: "xp.contact.lead_form.control",
    routeTemplateId: "contact",
    routePattern: "/contact",
    routePaths: ["/contact"],
    componentId: "contact.direct-brief.form",
    componentFamily: "lead_form",
    xpZone: "panel",
    xpDepth: 3,
    contentTheme: "direct_brief",
    campaignKey: "camp.contact.direct-brief.control",
    primaryGoalEvent: "cta_engaged",
  }),
  controlVariant({
    experienceId: "xp.proof.review-platforms.control",
    routeTemplateId: "global",
    routePattern: "/*",
    routePaths: ["/*"],
    componentId: "proof.review-platforms",
    componentFamily: "proof_review",
    xpZone: "card",
    xpDepth: 2,
    contentTheme: "proof",
    campaignKey: "camp.proof.reviews.control",
    primaryGoalEvent: "cta_engaged",
  }),
  controlVariant({
    experienceId: "xp.packages.configurator.control",
    routeTemplateId: "packages",
    routePattern: "/packages",
    routePaths: ["/packages"],
    componentId: "packages.configurator",
    componentFamily: "pricing_configurator",
    xpZone: "panel",
    xpDepth: 2,
    contentTheme: "packages",
    campaignKey: "camp.packages.configurator.control",
    primaryGoalEvent: "cta_engaged",
  }),
  controlVariant({
    experienceId: "xp.resources.lead_magnet.control",
    routeTemplateId: "resources/[slug]",
    routePattern: "/resources/*",
    routePaths: ["/resources/*"],
    componentId: "resources.lead-magnet",
    componentFamily: "lead_magnet",
    xpZone: "hero",
    xpDepth: 2,
    contentTheme: "resources",
    campaignKey: "camp.resources.lead-magnet.control",
    primaryGoalEvent: "cta_engaged",
  }),
  controlVariant({
    experienceId: "xp.funnel-planner.wizard.control",
    routeTemplateId: "funnel-planner",
    routePattern: "/funnel-planner",
    routePaths: ["/funnel-planner"],
    componentId: "funnel-planner.wizard",
    componentFamily: "funnel_planner",
    xpZone: "progress",
    xpDepth: 3,
    contentTheme: "funnel_planner",
    campaignKey: "camp.funnel-planner.wizard.control",
    primaryGoalEvent: "component_engaged",
  }),
  controlVariant({
    experienceId: "xp.funnel-planner.contact-capture.control",
    routeTemplateId: "funnel-planner",
    routePattern: "/funnel-planner",
    routePaths: ["/funnel-planner"],
    componentId: "funnel-planner.contact-capture.form",
    componentFamily: "lead_form",
    xpZone: "panel",
    xpDepth: 3,
    contentTheme: "funnel_planner",
    campaignKey: "camp.funnel-planner.contact.control",
    primaryGoalEvent: "cta_engaged",
  }),
  controlVariant({
    experienceId: "xp.funnel-planner.success.control",
    routeTemplateId: "funnel-planner",
    routePattern: "/funnel-planner",
    routePaths: ["/funnel-planner"],
    componentId: "funnel-planner.success",
    componentFamily: "funnel_planner",
    xpZone: "panel",
    xpDepth: 3,
    contentTheme: "funnel_planner",
    campaignKey: "camp.funnel-planner.success.control",
    primaryGoalEvent: "cta_engaged",
  }),
  controlVariant({
    experienceId: "xp.adult.compliance.control",
    routeTemplateId: "adult",
    routePattern: "/adult*",
    routePaths: ["/adult", "/adult/*"],
    componentId: "adult.compliance",
    componentFamily: "adult_compliance",
    xpZone: "hero",
    xpDepth: 3,
    contentTheme: "adult_compliance",
    campaignKey: "camp.adult.compliance.control",
    primaryGoalEvent: "cta_engaged",
    adultReviewRequired: true,
    vertical18Plus: true,
    marketingComplianceGate: "control_static",
  }),
] as const satisfies readonly ExperienceVariantRegistryRow[];

const REGISTRY_WITHOUT_CHECKSUM = {
  kind: EXPERIENCE_VARIANT_REGISTRY_KIND,
  registry_version: EXPERIENCE_VARIANT_REGISTRY_VERSION,
  generated_at: EXPERIENCE_VARIANT_REGISTRY_GENERATED_AT,
  source_crm_version: EXPERIENCE_VARIANT_REGISTRY_SOURCE_CRM_VERSION,
  checksum_algorithm: EXPERIENCE_VARIANT_REGISTRY_CHECKSUM_ALGORITHM,
  max_age_seconds: EXPERIENCE_VARIANT_REGISTRY_MAX_AGE_SECONDS,
  fallback_policy: "control_static",
  variants: CONTROL_VARIANTS,
} as const;

export const EXPERIENCE_VARIANT_REGISTRY_EXPORT = {
  ...REGISTRY_WITHOUT_CHECKSUM,
  checksum: "sha256:f231e923eb6eec6f20f406c96190e263b1e4a9e0a7d672037010ff1c5818fa3a",
} as const satisfies ExperienceVariantRegistryExport;

export const EXPERIENCE_CONTROL_VARIANT = CONTROL_VARIANTS[0];

export function buildExperienceVariantRegistryJson(registry: ExperienceVariantRegistryExport = EXPERIENCE_VARIANT_REGISTRY_EXPORT) {
  return JSON.stringify(toBrowserSafeExperienceVariantRegistry(registry)).replace(/</g, "\\u003c");
}

export function toBrowserSafeExperienceVariantRegistry(registry: ExperienceVariantRegistryExport): ExperienceVariantRegistryExport {
  return {
    kind: registry.kind,
    registry_version: registry.registry_version,
    generated_at: registry.generated_at,
    source_crm_version: registry.source_crm_version,
    checksum_algorithm: registry.checksum_algorithm,
    checksum: registry.checksum,
    max_age_seconds: registry.max_age_seconds,
    fallback_policy: registry.fallback_policy,
    variants: registry.variants.map((variant) => ({ ...variant })),
  };
}

export function resolveExperienceVariant(
  registry: unknown,
  query: ExperienceVariantQuery = {},
  options: ExperienceVariantResolveOptions = {},
): ExperienceVariantRegistryRow {
  if (!isExperienceVariantRegistryExport(registry) || options.checksumValid === false) {
    return EXPERIENCE_CONTROL_VARIANT;
  }

  const now = options.now ?? new Date();
  const liveTreatmentFresh = isExperienceVariantRegistryFresh(registry, now);
  const candidates = registry.variants.filter((variant) => variant.status === "active");
  const matched = candidates.find((variant) => variantMatchesQuery(variant, query)) ?? candidates[0];

  if (!matched) {
    return EXPERIENCE_CONTROL_VARIANT;
  }

  if (matched.variant_key !== "control" && !liveTreatmentFresh) {
    return EXPERIENCE_CONTROL_VARIANT;
  }

  if (!isAdultVariantAllowed(matched, liveTreatmentFresh)) {
    return EXPERIENCE_CONTROL_VARIANT;
  }

  return matched;
}

export function isExperienceVariantRegistryFresh(registry: ExperienceVariantRegistryExport, now = new Date()) {
  if (registry.source_crm_version === EXPERIENCE_VARIANT_REGISTRY_SOURCE_CRM_VERSION) {
    return true;
  }

  const generatedAtMs = Date.parse(registry.generated_at);
  if (!Number.isFinite(generatedAtMs) || registry.max_age_seconds <= 0) {
    return false;
  }

  return now.getTime() - generatedAtMs <= registry.max_age_seconds * 1000;
}

export function isExperienceVariantRegistryExport(value: unknown): value is ExperienceVariantRegistryExport {
  if (!value || typeof value !== "object") return false;
  const registry = value as Partial<ExperienceVariantRegistryExport>;
  return (
    registry.kind === EXPERIENCE_VARIANT_REGISTRY_KIND &&
    typeof registry.registry_version === "string" &&
    typeof registry.generated_at === "string" &&
    typeof registry.source_crm_version === "string" &&
    registry.checksum_algorithm === EXPERIENCE_VARIANT_REGISTRY_CHECKSUM_ALGORITHM &&
    typeof registry.checksum === "string" &&
    registry.checksum.startsWith("sha256:") &&
    Number.isFinite(registry.max_age_seconds) &&
    registry.fallback_policy === "control_static" &&
    Array.isArray(registry.variants) &&
    registry.variants.every(isExperienceVariantRegistryRow)
  );
}

export function isExperienceVariantRegistryRow(value: unknown): value is ExperienceVariantRegistryRow {
  if (!value || typeof value !== "object") return false;
  const row = value as Partial<ExperienceVariantRegistryRow>;
  return (
    typeof row.experience_id === "string" &&
    typeof row.route_template_id === "string" &&
    typeof row.route_pattern === "string" &&
    typeof row.component_id === "string" &&
    typeof row.component_family === "string" &&
    row.status === "active" &&
    typeof row.variant_key === "string" &&
    isSafeRegistryToken(row.variant_key) &&
    Array.isArray(row.route_paths) &&
    Array.isArray(row.allowed_motion_modes) &&
    Array.isArray(row.allowed_device_shells) &&
    Array.isArray(row.locale_scope) &&
    typeof row.primary_goal_event === "string" &&
    typeof row.adult_review_required === "boolean" &&
    typeof row.vertical_18plus === "boolean" &&
    typeof row.marketing_compliance_gate === "string"
  );
}

export function canonicalizeExperienceVariantRegistry(value: unknown): string {
  return JSON.stringify(sortForCanonicalJson(value));
}

export function registryChecksumInput(registry: ExperienceVariantRegistryExport): string {
  const { checksum, ...withoutChecksum } = registry;
  void checksum;
  return canonicalizeExperienceVariantRegistry(withoutChecksum);
}

function controlVariant({
  adultReviewRequired = false,
  campaignKey,
  componentFamily,
  componentId,
  contentTheme,
  experienceId,
  marketingComplianceGate = "not_applicable",
  primaryGoalEvent,
  routePaths,
  routePattern,
  routeTemplateId,
  secondaryGoalEvent,
  vertical18Plus = false,
  xpDepth,
  xpZone,
}: {
  readonly adultReviewRequired?: boolean;
  readonly campaignKey: string;
  readonly componentFamily: string;
  readonly componentId: string;
  readonly contentTheme: string;
  readonly experienceId: string;
  readonly marketingComplianceGate?: ExperienceRegistryComplianceGate;
  readonly primaryGoalEvent: ExperienceRegistryGoalEvent;
  readonly routePaths: readonly string[];
  readonly routePattern: string;
  readonly routeTemplateId: string;
  readonly secondaryGoalEvent?: ExperienceRegistryGoalEvent;
  readonly vertical18Plus?: boolean;
  readonly xpDepth: number;
  readonly xpZone: ExperienceVariantRegistryRow["xp_zone"];
}): ExperienceVariantRegistryRow {
  return {
    experience_id: experienceId,
    status: "active",
    route_template_id: routeTemplateId,
    route_pattern: routePattern,
    route_paths: routePaths,
    component_id: componentId,
    component_family: componentFamily,
    xp_zone: xpZone,
    xp_depth: xpDepth,
    allowed_motion_modes: ALL_MOTION_MODES,
    allowed_device_shells: BROWSER_SAFE_SHELLS,
    locale_scope: BROWSER_SAFE_LOCALES,
    content_theme: contentTheme,
    campaign_key: campaignKey,
    variant_key: "control",
    primary_goal_event: primaryGoalEvent,
    secondary_goal_event: secondaryGoalEvent,
    adult_review_required: adultReviewRequired,
    vertical_18plus: vertical18Plus,
    marketing_compliance_gate: marketingComplianceGate,
  };
}

function isSafeRegistryToken(value: string) {
  return /^[a-z0-9][a-z0-9_.:-]{0,79}$/i.test(value);
}

function variantMatchesQuery(variant: ExperienceVariantRegistryRow, query: ExperienceVariantQuery) {
  if (query.componentId && !matchesComponentId(variant.component_id, query.componentId)) {
    return false;
  }

  if (query.routeTemplateId && !matchesRouteTemplate(variant, query.routeTemplateId)) {
    return false;
  }

  if (query.locale && !variant.locale_scope.includes(query.locale as AppLocale)) {
    return false;
  }

  if (query.shell && !variant.allowed_device_shells.includes(query.shell as DeviceShell)) {
    return false;
  }

  if (query.motionMode && !variant.allowed_motion_modes.includes(query.motionMode as ExperienceRegistryMotionMode)) {
    return false;
  }

  return true;
}

function matchesComponentId(pattern: string, componentId: string) {
  if (pattern === componentId) return true;
  if (!pattern.includes("*")) return componentId.startsWith(pattern);
  const escaped = pattern.split("*").map(escapeRegExp).join(".*");
  return new RegExp(`^${escaped}$`).test(componentId);
}

function matchesRouteTemplate(variant: ExperienceVariantRegistryRow, routeTemplateId: string) {
  if (variant.route_template_id === routeTemplateId) return true;
  if (variant.route_template_id.endsWith("/[slug]")) {
    return routeTemplateId.startsWith(`${variant.route_template_id.replace("/[slug]", "")}/`);
  }
  if (variant.route_pattern.endsWith("*")) {
    return routeTemplateId.startsWith(variant.route_pattern.replace(/^\/+|\/?\*$/g, ""));
  }
  return variant.route_paths.includes("/*");
}

function isAdultVariantAllowed(variant: ExperienceVariantRegistryRow, liveTreatmentFresh: boolean) {
  if (!variant.vertical_18plus && !variant.adult_review_required) {
    return true;
  }

  if (variant.variant_key === "control" && variant.marketing_compliance_gate === "control_static") {
    return true;
  }

  return liveTreatmentFresh && variant.marketing_compliance_gate === "passed";
}

function sortForCanonicalJson(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortForCanonicalJson);
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, childValue]) => childValue !== undefined)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([key, childValue]) => [key, sortForCanonicalJson(childValue)]),
    );
  }

  return value;
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
